import type {
  CandidateType,
  CodecMetrics,
  ConnectionMetrics,
  ConnectionPath,
  QualityLimitationReason,
  SelectedCandidatePairMetrics,
} from './types';

export interface StatsCounterBaseline {
  readonly collectedAt: number;
  readonly bytesSent: number | null;
  readonly packetsSent: number | null;
  readonly packetsForLoss: number | null;
  readonly packetsLost: number | null;
  readonly framesEncoded: number | null;
}

export interface NormalizedStatsResult {
  readonly metrics: ConnectionMetrics;
  readonly baseline: StatsCounterBaseline;
}

type StatsRecord = Readonly<Record<string, unknown>> & {
  readonly id: string;
  readonly type: string;
};

export function normalizeStatsReport(
  report: RTCStatsReport,
  collectedAt: number,
  previous: StatsCounterBaseline | null,
): NormalizedStatsResult {
  const records = collectRecords(report);
  const outbound = records.filter(
    (record) =>
      record.type === 'outbound-rtp' &&
      readBoolean(record, 'isRemote') !== true &&
      readBoolean(record, 'active') !== false,
  );
  const videoOutbound = outbound.filter((record) => mediaKind(record) === 'video');
  const primaryOutboundVideo = maximumBy(
    videoOutbound,
    (record) => readNumber(record, 'bytesSent') ?? 0,
  );
  const inbound = records.filter(
    (record) => record.type === 'inbound-rtp' && readBoolean(record, 'isRemote') !== true,
  );
  const videoInbound = inbound.filter((record) => mediaKind(record) === 'video');
  const primaryVideo =
    primaryOutboundVideo ??
    maximumBy(videoInbound, (record) => readNumber(record, 'bytesReceived') ?? 0);
  const remoteInbound = records.filter(
    (record) => record.type === 'remote-inbound-rtp' && readBoolean(record, 'isRemote') !== true,
  );
  const selected = findSelectedPair(records);
  const localCandidate = findById(records, selected?.localCandidateId ?? null);
  const remoteCandidate = findById(records, selected?.remoteCandidateId ?? null);
  const localCandidateType = candidateType(
    localCandidate,
    selected === null ? null : readString(selected.record, 'googLocalCandidateType'),
  );
  const remoteCandidateType = candidateType(
    remoteCandidate,
    selected === null ? null : readString(selected.record, 'googRemoteCandidateType'),
  );

  const packetsSent = sumNumbers(outbound, 'packetsSent');
  const remotePacketsLost = sumNumbers(remoteInbound, 'packetsLost', true);
  const inboundPacketsLost = sumNumbers(inbound, 'packetsLost', true);
  const packetsLost =
    remotePacketsLost ??
    inboundPacketsLost ??
    sumNumbers(outbound, 'packetsLost', true) ??
    sumLegacyLost(records);
  const packetsForLoss =
    remotePacketsLost !== null
      ? packetsSent
      : inboundPacketsLost !== null
        ? sumNumbers(inbound, 'packetsReceived')
        : packetsLost !== null
          ? packetsSent
          : null;
  const bytesSent = sumNumbers(outbound, 'bytesSent');
  const framesEncoded = sumNumbers(videoOutbound, 'framesEncoded');
  const framesSent = sumNumbers(videoOutbound, 'framesSent');
  const framesDropped =
    sumNumbers(videoOutbound, 'framesDropped') ??
    sumNumbers(videoOutbound, 'framesDiscardedOnSend') ??
    sumNumbers(videoOutbound, 'framesDiscardedBeforeEncode') ??
    sumNumbers(videoOutbound, 'droppedFrames') ??
    sumNumbers(videoInbound, 'framesDropped');
  const sampleDurationMs =
    previous === null ? null : positiveDelta(collectedAt, previous.collectedAt);
  const packetDelta = counterDelta(packetsForLoss, previous?.packetsForLoss ?? null);
  const lostDelta = counterDelta(packetsLost, previous?.packetsLost ?? null);
  const byteDelta = counterDelta(bytesSent, previous?.bytesSent ?? null);
  const frameDelta = counterDelta(framesEncoded, previous?.framesEncoded ?? null);
  const directFps = readNumber(
    primaryVideo,
    'framesPerSecond',
    'googFrameRateSent',
    'googFrameRateReceived',
  );

  const metrics: ConnectionMetrics = {
    collectedAt,
    sampleDurationMs,
    rttMs: selected?.rttMs ?? secondsToMilliseconds(maxNumber(remoteInbound, 'roundTripTime')),
    packetsSent,
    packetsLost,
    packetLossPercentage: calculatePacketLoss(
      previous === null ? packetsForLoss : packetDelta,
      previous === null ? packetsLost : lostDelta,
    ),
    jitterMs: secondsToMilliseconds(maxNumber([...remoteInbound, ...inbound], 'jitter')),
    outboundBitrateBps: ratePerSecond(byteDelta, sampleDurationMs, 8),
    availableOutgoingBitrateBps:
      selected === null
        ? null
        : readNumber(selected.record, 'availableOutgoingBitrate', 'googAvailableSendBandwidth'),
    framesEncoded,
    framesSent,
    framesDropped,
    fps: directFps ?? ratePerSecond(frameDelta, sampleDurationMs, 1),
    frameWidth: readNumber(primaryVideo, 'frameWidth', 'googFrameWidthSent'),
    frameHeight: readNumber(primaryVideo, 'frameHeight', 'googFrameHeightSent'),
    qualityLimitationReason: qualityLimitationReason(primaryOutboundVideo),
    codec: findCodec(records, primaryVideo),
    selectedCandidatePair: selectedPairMetrics(selected, localCandidate),
    localCandidateType,
    remoteCandidateType,
    connectionPath: classifyConnectionPath(selected, localCandidateType, remoteCandidateType),
    retransmittedPacketsSent: sumNumbers(outbound, 'retransmittedPacketsSent'),
    retransmittedBytesSent: sumNumbers(outbound, 'retransmittedBytesSent'),
  };

  return {
    metrics: freezeMetrics(metrics),
    baseline: {
      collectedAt,
      bytesSent,
      packetsSent,
      packetsForLoss,
      packetsLost,
      framesEncoded,
    },
  };
}

function collectRecords(report: RTCStatsReport): StatsRecord[] {
  const records: StatsRecord[] = [];
  report.forEach((value) => {
    if (isRecord(value) && typeof value.id === 'string' && typeof value.type === 'string') {
      records.push({ ...value, type: canonicalStatsType(value.type) } as StatsRecord);
    }
  });
  return records;
}

interface SelectedPair {
  readonly record: StatsRecord;
  readonly localCandidateId: string | null;
  readonly remoteCandidateId: string | null;
  readonly rttMs: number | null;
}

function findSelectedPair(records: readonly StatsRecord[]): SelectedPair | null {
  const pairId =
    records
      .filter((record) => record.type === 'transport')
      .map((record) => readString(record, 'selectedCandidatePairId'))
      .find((value): value is string => value !== null) ?? null;
  const pairs = records.filter(
    (record) => record.type === 'candidate-pair' || record.type === 'googCandidatePair',
  );
  const pair =
    findById(pairs, pairId) ??
    pairs.find((record) => readBoolean(record, 'selected', 'googActiveConnection') === true) ??
    pairs.find(
      (record) =>
        readBoolean(record, 'nominated') === true && readString(record, 'state') === 'succeeded',
    ) ??
    null;
  if (pair === null) {
    return null;
  }

  const secondsRtt = readNumber(pair, 'currentRoundTripTime');
  return {
    record: pair,
    localCandidateId: readString(pair, 'localCandidateId', 'googLocalCandidateId', 'localId'),
    remoteCandidateId: readString(pair, 'remoteCandidateId', 'googRemoteCandidateId', 'remoteId'),
    rttMs: secondsRtt === null ? readNumber(pair, 'googRtt') : secondsToMilliseconds(secondsRtt),
  };
}

function selectedPairMetrics(
  selected: SelectedPair | null,
  localCandidate: StatsRecord | null,
): SelectedCandidatePairMetrics | null {
  if (selected === null) {
    return null;
  }
  return Object.freeze({
    id: selected.record.id,
    localCandidateId: selected.localCandidateId,
    remoteCandidateId: selected.remoteCandidateId,
    state: readString(selected.record, 'state'),
    nominated: readBoolean(selected.record, 'nominated', 'selected', 'googActiveConnection'),
    protocol:
      readString(localCandidate, 'protocol') ?? readString(selected.record, 'googTransportType'),
    relayProtocol: readString(localCandidate, 'relayProtocol'),
  });
}

function findCodec(
  records: readonly StatsRecord[],
  primaryVideo: StatsRecord | null,
): CodecMetrics | null {
  const codecId = readString(primaryVideo, 'codecId');
  const codec = findById(records, codecId);
  const mimeType =
    readString(codec, 'mimeType') ?? readString(primaryVideo, 'codec', 'googCodecName');
  if (mimeType === null) {
    return null;
  }
  return Object.freeze({
    mimeType,
    payloadType: readNumber(codec, 'payloadType'),
    clockRate: readNumber(codec, 'clockRate'),
    channels: readNumber(codec, 'channels'),
    sdpFmtpLine: readString(codec, 'sdpFmtpLine'),
  });
}

function qualityLimitationReason(record: StatsRecord | null): QualityLimitationReason | null {
  const value = readString(record, 'qualityLimitationReason');
  if (value === null) {
    return null;
  }
  return value === 'none' || value === 'cpu' || value === 'bandwidth' || value === 'other'
    ? value
    : 'unknown';
}

function candidateType(record: StatsRecord | null, fallback: string | null): CandidateType | null {
  const value = readString(record, 'candidateType', 'googCandidateType') ?? fallback;
  if (value === null) {
    return null;
  }
  return value === 'host' || value === 'srflx' || value === 'prflx' || value === 'relay'
    ? value
    : 'unknown';
}

function classifyConnectionPath(
  selected: SelectedPair | null,
  localType: CandidateType | null,
  remoteType: CandidateType | null,
): ConnectionPath {
  if (selected === null) {
    return 'unknown';
  }
  if (localType === 'relay' || remoteType === 'relay') {
    return 'relay';
  }
  if (
    localType !== null &&
    remoteType !== null &&
    localType !== 'unknown' &&
    remoteType !== 'unknown'
  ) {
    return 'direct';
  }
  return 'unknown';
}

function mediaKind(record: StatsRecord): string | null {
  return readString(record, 'kind', 'mediaType');
}

function canonicalStatsType(type: string): string {
  switch (type.toLowerCase()) {
    case 'outboundrtp':
      return 'outbound-rtp';
    case 'inboundrtp':
      return 'inbound-rtp';
    case 'remoteinboundrtp':
      return 'remote-inbound-rtp';
    case 'remoteoutboundrtp':
      return 'remote-outbound-rtp';
    case 'localcandidate':
      return 'local-candidate';
    case 'remotecandidate':
      return 'remote-candidate';
    default:
      return type;
  }
}

function sumNumbers(
  records: readonly StatsRecord[],
  key: string,
  clampToZero = false,
): number | null {
  const values = records
    .map((record) => readNumber(record, key))
    .filter((value): value is number => value !== null);
  if (values.length === 0) {
    return null;
  }
  return values.reduce((sum, value) => sum + (clampToZero ? Math.max(0, value) : value), 0);
}

function sumLegacyLost(records: readonly StatsRecord[]): number | null {
  return sumNumbers(
    records.filter((record) => record.type === 'ssrc' && readBoolean(record, 'googRemoteSource')),
    'packetsLost',
    true,
  );
}

function maxNumber(records: readonly StatsRecord[], key: string): number | null {
  const values = records
    .map((record) => readNumber(record, key))
    .filter((value): value is number => value !== null);
  return values.length === 0 ? null : Math.max(...values);
}

function maximumBy(
  records: readonly StatsRecord[],
  score: (record: StatsRecord) => number,
): StatsRecord | null {
  return records.reduce<StatsRecord | null>(
    (best, record) => (best === null || score(record) > score(best) ? record : best),
    null,
  );
}

function findById(records: readonly StatsRecord[], id: string | null): StatsRecord | null {
  return id === null ? null : (records.find((record) => record.id === id) ?? null);
}

function readNumber(record: StatsRecord | null, ...keys: string[]): number | null {
  for (const key of keys) {
    const value = record?.[key];
    const number = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
    if (typeof number === 'number' && Number.isFinite(number)) {
      return number;
    }
  }
  return null;
}

function readString(record: StatsRecord | null, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = record?.[key];
    if (typeof value === 'string' && value !== '') {
      return value;
    }
  }
  return null;
}

function readBoolean(record: StatsRecord | null, ...keys: string[]): boolean | null {
  for (const key of keys) {
    const value = record?.[key];
    if (typeof value === 'boolean') {
      return value;
    }
    if (value === 'true') {
      return true;
    }
    if (value === 'false') {
      return false;
    }
  }
  return null;
}

function counterDelta(current: number | null, previous: number | null): number | null {
  if (current === null || previous === null || current < previous) {
    return null;
  }
  return current - previous;
}

function positiveDelta(current: number, previous: number): number | null {
  const difference = current - previous;
  return difference > 0 ? difference : null;
}

function ratePerSecond(
  delta: number | null,
  durationMs: number | null,
  multiplier: number,
): number | null {
  return delta === null || durationMs === null ? null : (delta * multiplier * 1_000) / durationMs;
}

function calculatePacketLoss(sent: number | null, lost: number | null): number | null {
  if (sent === null || lost === null || sent + lost <= 0) {
    return null;
  }
  return (lost / (sent + lost)) * 100;
}

function secondsToMilliseconds(value: number | null): number | null {
  return value === null ? null : value * 1_000;
}

function freezeMetrics(metrics: ConnectionMetrics): ConnectionMetrics {
  return Object.freeze(metrics);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
